import { normalizeExternalProfileUrl } from "../../utils/externalProfileUrl";

export const AUTH_TOKEN_LS_KEY = "ouroAuthToken";
export const LEGACY_GOOGLE_TOKEN_LS_KEY = "ouroGoogleToken";
const MIN_INTERNATIONAL_DIGITS = 8;
export const MAX_INTERNATIONAL_PHONE_DIGITS = 15;
const INTERNATIONAL_PHONE_BODY_PATTERN = /^\+[0-9().\s-]+$/;
const PHONE_VERIFICATION_SENDABLE_ACCOUNT_STATUSES = new Set([
  "pending_review",
  "verified",
  "active",
]);
const PHONE_VERIFICATION_SENDABLE_STATUSES = new Set([
  null,
  "not_started",
  "phone_verification_ready",
  "expired",
]);
export const NICKNAME_MIN_LENGTH = 3;
export const NICKNAME_MAX_LENGTH = 16;
export const NICKNAME_HINT =
  "(3-16 characters, special chars allowed: _ or -)";
const NICKNAME_PATTERN = /^[A-Za-z0-9_-]+$/;

export function getNicknameValidationError(value) {
  const nickname = String(value || "").trim();
  if (
    nickname.length < NICKNAME_MIN_LENGTH ||
    nickname.length > NICKNAME_MAX_LENGTH
  ) {
    return `Nickname must be between ${NICKNAME_MIN_LENGTH} and ${NICKNAME_MAX_LENGTH} characters.`;
  }
  if (!NICKNAME_PATTERN.test(nickname)) {
    return "Use letters, numbers, underscores, or hyphens only.";
  }
  return null;
}

export const PENDING_REVIEW_PROFILE_EDITABLE_FIELDS = [
  "name",
  "nickname",
  "dateOfBirth",
  "phoneNumber",
  "avatar",
  "gender",
  "showGenderOnProfile",
  "steamInventoryValue",
];

function padDatePart(value) {
  return String(value).padStart(2, "0");
}

function isValidCalendarDateParts(year, month, day) {
  const candidate = new Date(Date.UTC(year, month - 1, day));
  return (
    candidate.getUTCFullYear() === year &&
    candidate.getUTCMonth() === month - 1 &&
    candidate.getUTCDate() === day
  );
}

export function getAccountAuthToken(storage) {
  if (!storage || typeof storage.getItem !== "function") return null;

  return (
    storage.getItem(AUTH_TOKEN_LS_KEY) ||
    storage.getItem(LEGACY_GOOGLE_TOKEN_LS_KEY) ||
    null
  );
}

export function normalizeDateOfBirthValue(value) {
  if (!value) return "";

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${value.getFullYear()}-${padDatePart(value.getMonth() + 1)}-${padDatePart(
      value.getDate()
    )}`;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    const match = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : "";
  }

  return "";
}

export function formatDateOfBirthForSubmit(value) {
  return normalizeDateOfBirthValue(value);
}

export function getAccountNameParts(user) {
  const givenName = String(user?.given_name || user?.firstName || "").trim();
  const familyName = String(user?.family_name || user?.lastName || "").trim();
  if (givenName || familyName) {
    return { givenName, familyName };
  }

  const fullName = String(user?.name || "").trim();
  if (!fullName) return { givenName: "", familyName: "" };

  const spaceIndex = fullName.indexOf(" ");
  if (spaceIndex === -1) {
    return { givenName: fullName, familyName: "" };
  }

  return {
    givenName: fullName.slice(0, spaceIndex),
    familyName: fullName.slice(spaceIndex + 1).trim(),
  };
}

export function getAccountLegalName(user) {
  const { givenName, familyName } = getAccountNameParts(user);
  return `${givenName} ${familyName}`.trim();
}

export function formatDateOfBirthDisplay(value) {
  const normalized = normalizeDateOfBirthValue(value);
  if (!normalized) return "";

  const [year, month, day] = normalized.split("-").map(Number);
  if (!isValidCalendarDateParts(year, month, day)) return "";

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${months[month - 1]} ${day}, ${year}`;
}

export function formatDateOfBirthInputValue(value) {
  const normalized = normalizeDateOfBirthValue(value);
  if (!normalized) return "";

  const [year, month, day] = normalized.split("-");
  return `${month}/${day}/${year}`;
}

export function maskDateOfBirthInput(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 8);
  const month = digits.slice(0, 2);
  const day = digits.slice(2, 4);
  const year = digits.slice(4, 8);
  if (digits.length <= 2) return month;
  if (digits.length <= 4) return `${month}/${day}`;
  return `${month}/${day}/${year}`;
}

export function parseDateOfBirthInputValue(value) {
  const match = maskDateOfBirthInput(value).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return "";

  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  if (!isValidCalendarDateParts(year, month, day)) return "";

  return `${year}-${padDatePart(month)}-${padDatePart(day)}`;
}

export function calculateAgeFromDob(dobString) {
  const normalized = normalizeDateOfBirthValue(dobString);
  if (!normalized) return null;

  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!year || month < 1 || month > 12 || day < 1 || day > 31) return null;
  if (!isValidCalendarDateParts(year, month, day)) return null;

  const today = new Date();
  const nowY = today.getUTCFullYear();
  const nowM = today.getUTCMonth() + 1;
  const nowD = today.getUTCDate();

  let age = nowY - year;
  const hasHadBirthdayThisYear = nowM > month || (nowM === month && nowD >= day);
  if (!hasHadBirthdayThisYear) age -= 1;
  if (age < 0 || age > 150) return null;

  return age;
}

export function getInternationalPhoneValidationError(phoneNumber) {
  const raw =
    phoneNumber == null
      ? ""
      : typeof phoneNumber === "string"
        ? phoneNumber
        : String(phoneNumber);
  const trimmed = raw.trim();
  if (trimmed === "") {
    return "Missing cell phone number";
  }
  if (!trimmed.startsWith("+")) {
    return "Phone number must be in international format.";
  }
  if (!INTERNATIONAL_PHONE_BODY_PATTERN.test(trimmed)) {
    return "Phone number must be in international format.";
  }
  const digits = trimmed.slice(1).replace(/\D/g, "");
  if (
    digits.length < MIN_INTERNATIONAL_DIGITS ||
    digits.length > MAX_INTERNATIONAL_PHONE_DIGITS
  ) {
    return "Phone number must be in international format.";
  }
  return null;
}

export function isValidInternationalPhoneNumber(phoneNumber) {
  return getInternationalPhoneValidationError(phoneNumber) === null;
}

export function sanitizePhoneLocalNumber(value, maxDigits = MAX_INTERNATIONAL_PHONE_DIGITS) {
  const safeMaxDigits = Number.isFinite(maxDigits)
    ? Math.max(0, Math.floor(maxDigits))
    : MAX_INTERNATIONAL_PHONE_DIGITS;
  return String(value || "").replace(/\D/g, "").slice(0, safeMaxDigits);
}

export function canRequestPhoneVerificationCode(
  accountStatus,
  phoneVerificationStatus
) {
  const normalizedAccountStatus = String(accountStatus || "")
    .trim()
    .toLowerCase();
  const normalizedPhoneStatus =
    phoneVerificationStatus == null
      ? null
      : String(phoneVerificationStatus).trim().toLowerCase() || null;

  return (
    PHONE_VERIFICATION_SENDABLE_ACCOUNT_STATUSES.has(normalizedAccountStatus) &&
    PHONE_VERIFICATION_SENDABLE_STATUSES.has(normalizedPhoneStatus)
  );
}

export function isPendingReviewPhoneCorrectionMode(accountLike) {
  return false;
}

function requiresPhoneNumberForSave(accountLike) {
  return false;
}

function requiresGenderForSave(accountLike) {
  return false;
}

// Returns an alert string if invalid, else null. FACEIT profile URL remains optional.
export function getAccountSaveValidationError(accountLike) {
  const source = accountLike && typeof accountLike === "object" ? accountLike : {};

  const {
    nickname = "",
    steamProfileUrl = "",
    steamId64 = "",
    phoneNumber = "",
    gender = "",
  } = source;

  if (nickname === "") {
    return "Missing in-game nickname";
  }
  const nicknameError = getNicknameValidationError(nickname);
  if (nicknameError) {
    return nicknameError;
  }

  if (steamId64 === "") {
    return "You didn't insert your SteamID 64 from your Steam account page";
  }

  const steamProfileUrlStr =
    typeof steamProfileUrl === "string" ? steamProfileUrl : "";
  if (steamProfileUrlStr === "") {
    return "Missing Steam profile URL";
  }
  if (normalizeExternalProfileUrl(steamProfileUrlStr) === null) {
    return "Steam profile URL must be a valid http or https URL";
  }

  if (requiresPhoneNumberForSave(source)) {
    const phoneErr = getInternationalPhoneValidationError(phoneNumber);
    if (phoneErr) {
      return phoneErr;
    }
  }

  if (
    requiresGenderForSave(source) &&
    (!gender || (gender !== "male" && gender !== "female"))
  ) {
    return "Please select your gender";
  }

  return null;
}
